import React, { useState, useEffect } from 'react';
import { Card, CircularProgress, Box, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper, Dialog, DialogContent, DialogTitle, IconButton } from '@mui/material';
import { PieChart, Pie, Cell, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, RadarChart, PolarGrid, PolarAngleAxis, PolarRadiusAxis, Radar } from 'recharts';
import { apiCall } from '../main/ApiCall';
import '../css/main/Home.css';
import '../css/others/Table.css';
import { buildUserPayload } from '../main/buildUserPayload';
import CloseIcon from '@mui/icons-material/Close';
import { AuditScore, ProjectRadarData, AuditScoresResponse } from "../interFaces/interfaces";
const TMDashboard: React.FC = () => {
  const [data, setData] = useState<any>(null);
  const [auditScoresData, setAuditScoresData] = useState<AuditScoresResponse | null>(null);
  const clientId = localStorage.getItem("clientId");
  const [loading, setLoading] = useState({ auditSummary: true, findingsStatus: true, projectChanges: true, projectsAssigned: true, controlCounts: true, auditScores: true });
  const [cardErrors, setCardErrors] = useState({ auditSummary: false, findingsStatus: false, projectChanges: false, projectsAssigned: false, controlCounts: false, auditScores: false });
  const [selectedProject, setSelectedProject] = useState<{ projectId: string, auditId: string, projectName: string } | null>(null);
  const [radarDialogOpen, setRadarDialogOpen] = useState(false);
  const [selectedProjectRadarData, setSelectedProjectRadarData] = useState<ProjectRadarData[]>([]);
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [itemsPerPage, setItemsPerPage] = useState<number>(5);
  const [totalPages, setTotalPages] = useState<number>(0);
  const [totalElements, setTotalElements] = useState<number>(0);
  const COLORS = ['#FF6384', '#FF9F40', '#FFCD56', '#4BC0C0', '#36A2EB', '#9966FF', '#6d1629', '#C9CBCF'];
  const LoadingSpinner = ({ height = 200 }: { height?: number }) => (
    <Box display="flex" justifyContent="center" alignItems="center" height={height}>
      <CircularProgress />
    </Box>
  );
  const NoDataMessage = ({ height = 200 }: { height?: number }) => (
    <Box display="flex" justifyContent="center" alignItems="center" height={height}>
      <div className="no-data">No data available</div>
    </Box>
  );
  useEffect(() => {
    const fetchData = async () => {
      let requestBody: { field: string; operator: string; value: string }[] = [
        { field: 'clientId', operator: '=', value: clientId ?? '' },
      ];
      const payload = buildUserPayload();
      if (payload) {
        requestBody.push(payload);
      }
      try {
        setLoading({ auditSummary: true, findingsStatus: true, projectChanges: true, projectsAssigned: true, controlCounts: true, auditScores: true });
        setCardErrors({ auditSummary: false, findingsStatus: false, projectChanges: false, projectsAssigned: false, controlCounts: false, auditScores: false });
        const response = await apiCall('post', `/api/dashboard/summary?clientId=${clientId}`, requestBody);
        setData(response);
      } catch (err) {
        console.error('API Error:', err);
        setCardErrors({ auditSummary: true, findingsStatus: true, projectChanges: true, projectsAssigned: true, controlCounts: true, auditScores: true });
      } finally {
        setLoading(prev => ({ ...prev, auditSummary: false, findingsStatus: false, projectChanges: false, projectsAssigned: false, controlCounts: false }));
      }
    };
    fetchData();
  }, [clientId]);
  useEffect(() => {
    const fetchAuditScoresData = async () => {
      try {
        setLoading(prev => ({ ...prev, auditScores: true }));
        setCardErrors(prev => ({ ...prev, auditScores: false }));
        const auditScoresResponse = await apiCall("get", `/api/dashboard/audit-scores?clientId=${clientId}&page=${currentPage - 1}&size=${itemsPerPage}`);
        setAuditScoresData(auditScoresResponse);
        if (auditScoresResponse) {
          setTotalPages(auditScoresResponse.totalPages || 0);
          setTotalElements(auditScoresResponse.totalElements || 0);
        }
      } catch (err) {
        setCardErrors(prev => ({ ...prev, auditScores: true }));
      } finally {
        setLoading(prev => ({ ...prev, auditScores: false }));
      }
    };
    fetchAuditScoresData();
  }, [clientId, currentPage, itemsPerPage]);
  const renderNoData = (height = 200) => (
    <div className="no-data-available"> No data available</div>
  );
  const findingsData = data?.findings ? [
    { name: 'Not Implemented', value: data.findings?.notImplemented || 0, color: '#9E9E9E' },
    { name: 'Initial / Ad-hoc', value: data.findings?.initialAdhoc || 0, color: '#FFB74D' },
    { name: 'Partially Implemented', value: data.findings?.partiallyImplemented || 0, color: '#FFD54F' },
    { name: 'Implemented but Needs Improvement', value: data.findings?.improvements || 0, color: '#2196F3' },
    { name: 'Largely Effective', value: data.findings?.largelyEffective || 0, color: '#4DB6AC' },
    { name: 'Best Practice / Optimized', value: data.findings?.bestPractices || 0, color: '#4CAF50' },
  ] : [];
  const hasFindingsData = findingsData.some(item => item.value > 0);
  const findingsStatusData = data?.findings?.ncCurrentStatus ? [
    { name: 'Closed', value: data.findings.ncCurrentStatus?.closed || 0, color: '#4CAF50' },
    { name: 'Open', value: data.findings.ncCurrentStatus?.open || 0, color: '#FFC107' },
    { name: 'Deferred', value: data.findings.ncCurrentStatus?.deferred || 0, color: '#9C27B0' },
    { name: 'Work in Progress', value: data.findings.ncCurrentStatus?.work_in_progress || 0, color: '#b13e08ff' },
  ].filter(item => item.value > 0) : [];
  const controlCountsData = data?.controlCounts ? [
    { name: 'AIMS', value: data.controlCounts.aims || 0, color: '#8E24AA' },
    { name: 'QMS', value: data.controlCounts.qms || 0, color: '#D81B60' },
    { name: 'BCMS', value: data.controlCounts.bcms || 0, color: '#00897B' },
    { name: 'CMMI', value: data.controlCounts.cmmi || 0, color: '#5C6BC0' },
    { name: 'PIMS', value: data.controlCounts.pims || 0, color: '#F57C00' },
    { name: 'ISMS', value: data.controlCounts.isms || 0, color: '#43A047' },
    { name: 'SOC1', value: data.controlCounts.soc1 || 0, color: '#E53935' },
    { name: 'SOC2', value: data.controlCounts.soc2 || 0, color: '#039BE5' },
    { name: 'ERM', value: data.controlCounts.erm || 0, color: '#6D4C41' },
    { name: 'ITSM', value: data.controlCounts.itsm || 0, color: '#1dd5ee' },
    { name: 'ERS', value: data.controlCounts.ers || 0, color: '#9ef83f' },
    { name: 'OHS', value: data.controlCounts.ohs || 0, color: '#802c12' },
  ] : [];
  const hasControlCountsData = controlCountsData.some(item => item.value > 0);
  const formatDate = (dateString: string | null) => {
    if (!dateString) return 'N/A';
    try {
      const date = new Date(dateString);
      return isNaN(date.getTime()) ? 'Invalid Date' : date.toLocaleString(undefined, { year: 'numeric', month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit', hour12: true });
    } catch {
      return 'Invalid Date';
    }
  };
  const handleProjectClick = (projectId: string, auditId: string, projectName: string) => {
    setSelectedProject({ projectId, auditId, projectName });
    const projectAudit = auditScoresData?.content?.find(
      (audit: AuditScore) => audit?.auditId === auditId && audit?.projectId === projectId
    );
    if (projectAudit?.auditScoreData?.controlScores && Array.isArray(projectAudit.auditScoreData.controlScores)) {
      const radarData = projectAudit.auditScoreData.controlScores
        .filter(control => control?.controlType && control?.score !== undefined)
        .map((control, index) => ({ subject: control.controlType, score: control.score, weightedScore: control.weightedScore, fullMark: 5, color: COLORS[index % COLORS.length] }));
      setSelectedProjectRadarData(radarData);
      setRadarDialogOpen(true);
    }
  };
  const handleCloseRadarDialog = () => {
    setRadarDialogOpen(false);
    setSelectedProject(null);
    setSelectedProjectRadarData([]);
  };
  const getControlScoreByType = (auditScore: AuditScore, controlType: string) => {
    if (!auditScore?.auditScoreData?.controlScores || !Array.isArray(auditScore.auditScoreData.controlScores)) {
      return undefined;
    }
    return auditScore.auditScoreData.controlScores.find(
      (control) => control?.controlType === controlType
    );
  };
  const getAllControlTypes = () => {
    if (!auditScoresData?.content || !Array.isArray(auditScoresData.content)) return [];
    const allControlTypes = new Set<string>();
    auditScoresData.content.forEach((auditScore: AuditScore) => {
      if (auditScore?.auditScoreData?.controlScores && Array.isArray(auditScore.auditScoreData.controlScores)) {
        auditScore.auditScoreData.controlScores.forEach((control) => {
          if (control?.controlType) {
            allControlTypes.add(control.controlType);
          }
        });
      }
    });
    return Array.from(allControlTypes);
  };
  const allControlTypes = getAllControlTypes();
  const formatWeightedScore = (weightedScore: number): string => {
    return `${weightedScore}%`;
  };
  const handleFirstPage = () => {
    setCurrentPage(1);
  };
  const handleLastPage = () => {
    setCurrentPage(totalPages);
  };
  const handleNextPage = () => {
    if (currentPage < totalPages) {
      setCurrentPage(currentPage + 1);
    }
  };
  const handlePreviousPage = () => {
    if (currentPage > 1) {
      setCurrentPage(currentPage - 1);
    }
  };
  const onPageChange = (page: number) => {
    setCurrentPage(page);
  };
  const handleItemsPerPageChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
    setItemsPerPage(Number(event.target.value));
    setCurrentPage(1);
  };
  const startItem = (currentPage - 1) * itemsPerPage + 1;
  const endItem = Math.min(currentPage * itemsPerPage, totalElements);
  const pageNumbers = [];
  const maxPageNumbers = 5;
  let startPage = Math.max(1, currentPage - Math.floor(maxPageNumbers / 2));
  let endPage = Math.min(totalPages, startPage + maxPageNumbers - 1);
  if (endPage - startPage + 1 < maxPageNumbers) {
    startPage = Math.max(1, endPage - maxPageNumbers + 1);
  }
  for (let i = startPage; i <= endPage; i++) {
    pageNumbers.push(i);
  }
  return (
    <div className="dashboard">
      <div className="dashboard-header">
        <h1 className="dashboard-title">Test Manager Dashboard</h1>
        <div className="dashboard-last-login">Last login: {new Date().toLocaleString()}</div>
      </div>
      <div className="dashboard-grid">
        <Card className="dashboard-card">
          <h2 className="dashboard-card-title">Audit Summary</h2>
          {loading.auditSummary ? (
            <LoadingSpinner height={318} />
          ) : cardErrors.auditSummary || !data ? (
            renderNoData(318)
          ) : (
            <>
              <div className="dashboard-metric-row">
                <div className="dashboard-metric-item">
                  <span className="dashboard-metric-value">{data?.completedAudits ?? 0}</span>
                  <span className="dashboard-metric-label">Completed Audits</span>
                </div>
                <div className="dashboard-metric-item">
                  <span className="dashboard-metric-value">{data?.assignedAudits ?? 0}</span>
                  <span className="dashboard-metric-label">Assigned Audits</span>
                </div>
              </div>
              <div className="dashboard-chart-container">
                <div className="flex-1">
                  {hasFindingsData ? (
                    <ResponsiveContainer width="100%" height={200}>
                      <PieChart>
                        <Pie data={findingsData} cx="50%" cy="50%" outerRadius={50} dataKey="value" labelLine={false}>
                          {findingsData.map((entry, index) => (
                            <Cell key={`cell-${index}`} fill={entry.color} />
                          ))}
                        </Pie>
                        <Tooltip />
                      </PieChart>
                    </ResponsiveContainer>
                  ) : (
                    <NoDataMessage height={200} />
                  )}
                </div>
                <div className="flex-1">
                  {findingsData.map((entry, index) => (
                    <div key={`findings-legend-${index}-${entry.name}`} className="findings-legend-item">
                      <div className="findings-legend-color" style={{ backgroundColor: entry.color }} />
                      <span className="findings-legend-text"> {entry.name}: {entry.value}</span>
                    </div>
                  ))}
                </div>
              </div>
            </>
          )}
        </Card>
        <Card className="dashboard-card">
          <h2 className="dashboard-card-title">Findings Status</h2>
          {loading.findingsStatus ? (
            <LoadingSpinner height={200} />
          ) : cardErrors.findingsStatus || !data?.findings?.ncCurrentStatus ? (
            renderNoData(200)
          ) : (
            <div className="dashboard-chart-container">
              {findingsStatusData.length > 0 ? (
                <ResponsiveContainer width="90%" height={200} className="chart-mt-50">
                  <BarChart data={findingsStatusData}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="name" />
                    <YAxis />
                    <Tooltip />
                    <Legend />
                    <Bar dataKey="value">
                      {findingsStatusData.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={entry.color} />
                      ))}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              ) : (
                renderNoData(200)
              )}
            </div>
          )}
        </Card>
      </div>
      <div className="dashboard-grid mt-20">
        <Card className="dashboard-card">
          <h2 className="dashboard-card-title">Current Projects</h2>
          {loading.projectChanges ? (
            <LoadingSpinner height={200} />
          ) : cardErrors.projectChanges || !data?.currentProjectChanges ? (
            renderNoData(200)
          ) : (
            <>
              <div className="dashboard-metric-row">
                <div className="dashboard-metric-item">
                  <span className="dashboard-metric-value">{data.currentProjectChanges?.overall?.excluded ?? 0}</span>
                  <span className="dashboard-metric-label">Excluded Projects (Overall)</span>
                </div>
                <div className="dashboard-metric-item">
                  <span className="dashboard-metric-value">{data.currentProjectChanges?.overall?.additional ?? 0}</span>
                  <span className="dashboard-metric-label">Additional Projects (Overall)</span>
                </div>
              </div>
              <div className="dashboard-metric-row">
                <div className="dashboard-metric-item">
                  <span className="dashboard-metric-value">{data.currentProjectChanges?.currentCampaign?.excluded ?? 0}</span>
                  <span className="dashboard-metric-label">Excluded Projects (Current Campaign)</span>
                </div>
                <div className="dashboard-metric-item">
                  <span className="dashboard-metric-value">{data.currentProjectChanges?.currentCampaign?.additional ?? 0}</span>
                  <span className="dashboard-metric-label">Additional Projects (Current Campaign)</span>
                </div>
              </div>
            </>
          )}
        </Card>
        <Card className="dashboard-card">
          <h2 className="dashboard-card-title">Control Counts</h2>
          <div className="dashboard-metric-row">
            <div className="dashboard-metric-item">
              <span className="dashboard-metric-value">{data?.completedAudits ?? 0}</span>
              <span className="dashboard-metric-label">Completed Audits</span>
            </div>
            <div className="dashboard-metric-item">
              <span className="dashboard-metric-value">{data?.assignedAudits ?? 0}</span>
              <span className="dashboard-metric-label">Assigned Audits</span>
            </div>
          </div>
          {loading.controlCounts ? (
            <LoadingSpinner height={318} />
          ) : cardErrors.controlCounts || !data?.controlCounts ? (
            renderNoData(318)
          ) : (
            <div className="dashboard-chart-container">
              <div className="flex-1">
                {hasControlCountsData ? (
                  <ResponsiveContainer width="100%" height={200}>
                    <PieChart>
                      <Pie data={controlCountsData} cx="50%" cy="50%" outerRadius={50} dataKey="value" labelLine={false}>
                        {controlCountsData.map((entry, index) => (
                          <Cell key={`cell-${index}`} fill={entry.color} />
                        ))}
                      </Pie>
                      <Tooltip />
                    </PieChart>
                  </ResponsiveContainer>
                ) : (
                  <NoDataMessage height={200} />
                )}
              </div>
              <div className="flex-1">
                {controlCountsData.map((entry, index) => (
                  <div key={`controls-legend-${index}-${entry.name}`} className="controls-legend-item">
                    <div className="controls-legend-color" style={{ backgroundColor: entry.color }} />
                    <span className="controls-legend-text"> {entry.name}: {entry.value}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </Card>
      </div>
      <Card className="dashboard-card dashboard-full-width-card mt-20">
        <h2 className="dashboard-card-title">Projects Assigned</h2>
        {loading.projectsAssigned ? (
          <LoadingSpinner height={400} />
        ) : cardErrors.projectsAssigned || !data?.currentProjects ? (
          renderNoData(400)
        ) : (
          <div className="dashboard-projects-container">
            {Array.isArray(data.currentProjects) && data.currentProjects.length > 0 ? (
              data.currentProjects.map((project: any) => (
                <div key={`project-${project.projectId}`} className="dashboard-project-item">
                  <div className="dashboard-project-name">Project ID: {project.projectId}</div>
                  <div className="dashboard-project-name">Project Name: {project.projectName}</div>
                  <div className={`dashboard-project-status dashboard-project-status-${(project.status || '')
                    .toLowerCase()
                    .replace('_', '-')}`}> Project Status : {project.status?.replace('_', ' ')}
                  </div>
                  <div className="dashboard-project"> Start Date: {project.startDate ? formatDate(project.startDate) : 'Not Applicable'} </div>
                  <div className="dashboard-project">End Date: {project.endDate ? formatDate(project.endDate) : 'Not Applicable'}</div>
                  <div className="dashboard-project"> Report Submitted:{' '} {project.reportSubmitted === true ? 'Yes' : project.reportSubmitted === false ? 'No' : project.reportSubmitted === null ? 'Not Applicable' : formatDate(project.reportSubmitted)}</div>
                </div>
              ))
            ) : (
              renderNoData(400)
            )}
          </div>
        )}
      </Card>
      <nav className="admin-dashboard-section-title">Audit Performance Scores</nav>
      <Card className="dashboard-card dashboard-full-width-card">
        <h2 className="dashboard-card-title">Audit Scores</h2>
        {loading.auditScores ? (
          <LoadingSpinner height={400} />
        ) : cardErrors.auditScores || !auditScoresData?.content || auditScoresData.content.length === 0 ? (
          <div className="no-data-available">No audit scores data available</div>
        ) : (
          <>
            <TableContainer component={Paper} className="campaigns-table-container">
              <Table>
                <TableHead className="campaigns-table-header">
                  <TableRow>
                    <TableCell>Campaign Name</TableCell>
                    <TableCell>Project Name</TableCell>
                    <TableCell align="right">Overall Score</TableCell>
                    <TableCell align="center">Overall Rating</TableCell>
                    {allControlTypes.map((controlType, idx) => (
                      <React.Fragment key={`header-${controlType}-${idx}`}>
                        <TableCell align="right">{controlType} Weighted</TableCell>
                        <TableCell align="center">{controlType} Rating</TableCell>
                      </React.Fragment>
                    ))}
                    <TableCell align="center">View Radar</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody className="campaigns-table-body">
                  {auditScoresData.content.map((auditScore: AuditScore, index: number) => {
                    const overallScore = auditScore.auditScoreData?.overallScore || 0;
                    const overallRating = auditScore.auditScoreData?.overallRating || 'N/A';
                    const getRatingClassName = (rating: string) => {
                      if (!rating) return "rating-default";
                      switch (rating.toLowerCase()) {
                        case "high risk":
                          return "rating-high-risk";
                        case "needs improvement":
                          return "rating-needs-improvement";
                        case "satisfactory":
                          return "rating-satisfactory";
                        case "effective":
                          return "rating-effective";
                        case "optimized":
                          return "rating-optimized";
                        default:
                          return "rating-default";
                      }
                    };
                    const overallRatingClassName = getRatingClassName(overallRating);
                    return (
                      <TableRow key={`${auditScore.auditId}-${auditScore.projectId}-${index}`} hover className="campaign-body-row">
                        <TableCell>{auditScore.campaignName}</TableCell>
                        <TableCell>{auditScore.projectName}</TableCell>
                        <TableCell align="right">
                          <span className="overall-score">{overallScore.toFixed(2)}%</span>
                        </TableCell>
                        <TableCell align="center">
                          <span className={`rating-badge ${overallRatingClassName}`}>{overallRating} </span>
                        </TableCell>
                        {allControlTypes.map((controlType) => {
                          const control = getControlScoreByType(auditScore, controlType);
                          const ratingClassName = getRatingClassName(control?.rating || '');
                          return (
                            <React.Fragment key={`${auditScore.auditId}-${controlType}`}>
                              <TableCell align="right">
                                <span className="weighted-score"> {control ? formatWeightedScore(control.weightedScore) : 'N/A'}  </span>
                              </TableCell>
                              <TableCell align="center">
                                <span className={`rating-badge ${ratingClassName}`}>  {control?.rating || 'N/A'} </span>
                              </TableCell>
                            </React.Fragment>
                          );
                        })}
                        <TableCell align="center">
                          {auditScore.checklistType !== "NORMAL" ? (
                            <button className="radar-view-button" onClick={() => handleProjectClick(auditScore.projectId, auditScore.auditId, auditScore.projectName)}>View Radar</button>
                          ) : (
                            <span className="no-data">-</span>
                          )}
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
            <div className="pagination">
              <button className="info">Showing {startItem} to {endItem} of {totalElements} items</button>
              <div className="pagination-controls">
                <button onClick={handleFirstPage} className="pagination-btn" disabled={currentPage === 1}>
                  <span className="small-pipe">|</span>&#8249;
                </button>
                <button onClick={handlePreviousPage} className="pagination-btn" disabled={currentPage === 1}>&#8249;</button>
                {pageNumbers.map((page) => (
                  <button key={`page-${page}`} onClick={() => onPageChange(page)} className={`pagination-btn ${currentPage === page ? "active" : ""}`}>{page}</button>
                ))}
                <button onClick={handleNextPage} className="pagination-btn" disabled={currentPage === totalPages}>&#8250;</button>
                <button onClick={handleLastPage} className="pagination-btn" disabled={currentPage === totalPages}>
                  &#8250;<span className="small-pipe">|</span>
                </button>
              </div>
              <div>
                <select id="itemsPerPageSelect" value={itemsPerPage} onChange={handleItemsPerPageChange} className="items-perpage-select">
                  <option value={5}>5</option>
                  <option value={25}>25</option>
                  <option value={50}>50</option>
                  <option value={100}>100</option>
                </select>
              </div>
            </div>
          </>
        )}
      </Card>
      <Dialog open={radarDialogOpen} onClose={handleCloseRadarDialog} maxWidth="lg" fullWidth>
        <DialogTitle className="radar-dialog-title">
          <div className="radar-dialog-header">
            <span>Control Performance Radar - Project: {selectedProject?.projectName}</span>
            <IconButton onClick={handleCloseRadarDialog} className="close-button">
              <CloseIcon />
            </IconButton>
          </div>
        </DialogTitle>
        <DialogContent className="radar-dialog-content">
          {selectedProjectRadarData.length > 0 ? (
            <ResponsiveContainer width="100%" height={500}>
              <RadarChart cx="50%" cy="50%" outerRadius="70%" data={selectedProjectRadarData}>
                <PolarGrid />
                <PolarAngleAxis dataKey="subject" />
                <PolarRadiusAxis angle={30} domain={[0, 5]} />
                <Radar name="Score" dataKey="score" stroke="#8884d8" strokeWidth={2} fill="#8884d8" fillOpacity={0.3} dot={(props: any) => {
                  const { cx, cy, index } = props;
                  return <circle key={index} cx={cx} cy={cy} r={5} fill={COLORS[index % COLORS.length]} stroke="#fff" strokeWidth={1} />;
                }} />
                <Radar name="Weighted Score" dataKey="weightedScore" stroke="#82ca9d" strokeWidth={2} fill="#82ca9d" fillOpacity={0.3} dot={(props: any) => {
                  const { cx, cy, index } = props;
                  return <circle key={index} cx={cx} cy={cy} r={5} fill={COLORS[index % COLORS.length]} stroke="#fff" strokeWidth={1} />;
                }} />
                <Legend />
                <Tooltip formatter={(value: number, name: string) => [value.toFixed(2), name === 'score' ? 'Score' : 'Weighted Score']} />
              </RadarChart>
            </ResponsiveContainer>
          ) : (
            <div className="no-data">No radar data available for this project</div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};
export default TMDashboard;